import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, Calendar, Check, ChevronRight, DollarSign, Loader2, LayoutGrid,
  LogOut, MapPin, MessageCircle, Star, Users2, Video, Wallet, Zap, CalendarClock,
  Settings, User, TrendingUp, X,
} from "lucide-react";
import { coachDashApi, DASH_TESTIDS as D } from "@/lib/coachDashApi";
import { StarsDisplay } from "@/components/coachly/StarRating";
import MarketplaceNav from "@/components/coachly/MarketplaceNav";
import CoachInbox from "@/components/coachly/CoachInbox";
import AvailabilityEditor from "@/components/coachly/AvailabilityEditor";
import MediaManager from "@/components/coachly/MediaManager";
import BundleManager from "@/components/coachly/BundleManager";
import IntroVideoManager from "@/components/coachly/IntroVideoManager";
import ProfileEditor from "@/components/coachly/ProfileEditor";
import { useAuth } from "@/context/AuthContext";

const STORAGE_KEY = "coachly_coach_email";

const TABS = [
  { id: "overview",     label: "Overview",     Icon: LayoutGrid },
  { id: "bookings",     label: "Bookings",     Icon: CalendarClock },
  { id: "availability", label: "Availability", Icon: Settings },
  { id: "earnings",     label: "Earnings",     Icon: Wallet },
  { id: "reviews",      label: "Reviews",      Icon: Star },
  { id: "profile",      label: "Profile",      Icon: User },
  { id: "messages",     label: "Messages",     Icon: MessageCircle },
];

function fmtMoney(cents = 0, currency = "usd") {
  return (cents / 100).toLocaleString("en-US", { style: "currency", currency: currency.toUpperCase() });
}
function fmtDate(iso) {
  return new Date(iso).toLocaleString("en-US", {
    weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

export default function CoachDashboard() {
  const { user, openSignIn } = useAuth();
  const [email, setEmail] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(() => {
    if (typeof window === "undefined") return "overview";
    const url = new URLSearchParams(window.location.search).get("tab");
    return TABS.find((t) => t.id === url)?.id || "overview";
  });

  // Auto-fill from Google session.
  useEffect(() => {
    if (user?.email && !email) setEmail(user.email);
    // eslint-disable-next-line
  }, [user?.email]);

  useEffect(() => {
    if (email) load(email);
    // eslint-disable-next-line
  }, [email]);

  // Sync tab to URL so refreshes and shared links land on the right pane.
  useEffect(() => {
    if (!data) return;
    const url = new URL(window.location.href);
    url.searchParams.set("tab", tab);
    window.history.replaceState({}, "", url.toString());
  }, [tab, data]);

  const load = async (e = email) => {
    setLoading(true);
    setError(null);
    try {
      const d = await coachDashApi.get(e);
      setData(d);
      sessionStorage.setItem(STORAGE_KEY, e);
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || "Failed to load dashboard.";
      // 403/404 → this signed-in user isn't an approved coach. Don't kick them
      // back to the login screen; show a friendly gate instead.
      if (status === 404 || status === 403) {
        setError({ status, msg });
        setData(null);
      } else {
        toast.error(msg);
        sessionStorage.removeItem(STORAGE_KEY);
        setEmail("");
        setData(null);
      }
    } finally { setLoading(false); }
  };

  const signOut = () => { sessionStorage.removeItem(STORAGE_KEY); setEmail(""); setData(null); setError(null); };

  if (error) return <NotACoachScreen email={email} error={error} onSignOut={signOut} />;

  if (!email || !data) {
    return (
      <LoginScreen
        initial={email}
        loading={loading}
        onSubmit={(e) => setEmail(e)}
        onGoogleSignIn={() => openSignIn("Sign in with the email your coach profile uses.")}
      />
    );
  }

  return (
    <DashboardScreen
      data={data}
      onRefresh={() => load()}
      onSignOut={signOut}
      email={email}
      tab={tab}
      onTabChange={setTab}
    />
  );
}

// ---------------------------------------------------------------------------
function LoginScreen({ initial, loading, onSubmit, onGoogleSignIn }) {
  const [value, setValue] = useState(initial || "");
  const submit = (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) return toast.error("Enter a valid email.");
    onSubmit(value.trim().toLowerCase());
  };
  return (
    <div data-testid={D.page} className="bg-white min-h-screen">
      <MarketplaceNav />
      <section className="max-w-[900px] mx-auto px-6 md:px-10 py-16 md:py-24 grid md:grid-cols-2 gap-10">
        <div>
          <div className="eyebrow">§ Coach dashboard</div>
          <h1 className="mt-4 font-display font-medium text-[42px] md:text-[64px] leading-[0.95] tracking-[-0.05em] text-slate-900">
            Your workspace, <span className="italic font-light text-[#2563EB]">at a glance.</span>
          </h1>
          <p className="mt-6 text-slate-600 text-lg leading-relaxed">
            Bookings, reviews, Stripe status and earnings — everything in one place.
          </p>
        </div>
        <motion.form
          onSubmit={submit}
          data-testid={D.loginForm}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-slate-200 rounded-2xl p-6 md:p-7 h-fit"
        >
          <button
            type="button"
            onClick={onGoogleSignIn}
            data-testid="coach-dash-google-signin"
            className="w-full h-12 rounded-full bg-slate-900 hover:bg-[#2563EB] text-white text-[13.5px] font-semibold inline-flex items-center justify-center gap-2 transition-colors"
          >
            Continue with Google →
          </button>
          <div className="my-4 flex items-center gap-3 text-[11px] text-slate-400 uppercase tracking-[0.24em]">
            <span className="h-px flex-1 bg-slate-200" /> or use email <span className="h-px flex-1 bg-slate-200" />
          </div>
          <label className="text-[11px] tracking-[0.24em] uppercase text-slate-500 font-semibold">Your Coachly email</label>
          <input
            type="email"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            data-testid={D.loginEmail}
            placeholder="you@domain.com"
            className="mt-2 w-full h-12 rounded-xl bg-slate-50 border border-slate-200 px-4 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25"
            required
          />
          <button
            type="submit"
            disabled={loading}
            data-testid={D.loginSubmit}
            className="mt-4 w-full h-12 rounded-full bg-white border border-slate-200 hover:border-slate-900 text-slate-900 text-[13.5px] font-semibold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Open my dashboard"}
            <ArrowRight className="w-4 h-4" strokeWidth={2.4} />
          </button>
          <p className="mt-3 text-[11px] text-slate-400">
            Google sign-in verifies your identity end-to-end.
          </p>
        </motion.form>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
function NotACoachScreen({ email, error, onSignOut }) {
  return (
    <div data-testid="coach-dashboard-not-coach" className="bg-white min-h-screen">
      <MarketplaceNav />
      <section className="max-w-[720px] mx-auto px-6 md:px-10 py-16 md:py-24 text-center">
        <div className="w-16 h-16 rounded-2xl bg-slate-100 grid place-items-center mx-auto">
          <Users2 className="w-7 h-7 text-slate-500" />
        </div>
        <div className="eyebrow mt-6">§ Not a coach account</div>
        <h1 className="mt-3 font-display font-medium text-3xl md:text-5xl tracking-tight text-slate-900">
          You're signed in as <span className="text-[#2563EB]">{email}</span> —{" "}
          <span className="italic font-light">but no coach profile is linked to that email.</span>
        </h1>
        <p className="mt-6 text-slate-600 text-lg leading-relaxed max-w-[52ch] mx-auto">
          The coach dashboard is private to approved Coachly coaches. If this is your business email, apply to join —
          our team reviews new coaches in 1–2 business days.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link to="/apply" data-testid="coach-dashboard-apply-cta" className="btn-primary h-12 px-6 rounded-full text-[13.5px] font-semibold inline-flex items-center gap-2">
            Apply to become a coach <ArrowRight className="w-4 h-4" strokeWidth={2.4} />
          </Link>
          <Link to="/marketplace" className="h-12 px-6 rounded-full text-[13.5px] font-semibold border border-slate-200 hover:border-slate-900 text-slate-900 inline-flex items-center gap-2">
            Browse coaches
          </Link>
          <button onClick={onSignOut} className="h-12 px-4 text-[13px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
        <p className="mt-6 text-[12.5px] text-slate-400">
          Signed in as a client? Head back to the <Link to="/marketplace" className="underline">marketplace</Link> — your bookings live under <Link to="/my-sessions" className="underline">My sessions</Link>.
        </p>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
function DashboardScreen({ data, onRefresh, onSignOut, email, tab, onTabChange }) {
  const { coach, stripe, bookings, reviews, earnings } = data;
  const upcoming = bookings.upcoming || [];
  const past = bookings.past || [];
  const cancelled = bookings.cancelled || [];

  const onCompleteBooking = async (id) => {
    try {
      await coachDashApi.completeBooking(email, id);
      toast.success("Booking marked completed. Client can now leave a review.");
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to complete booking.");
    }
  };

  const onOnboard = async () => {
    try {
      const r = await coachDashApi.startOnboarding(email);
      window.location.href = r.onboarding_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Onboarding failed.");
    }
  };

  return (
    <div data-testid={D.page} className="bg-slate-50 min-h-screen">
      <MarketplaceNav />
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 pt-10 md:pt-14 pb-16">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="eyebrow">§ Coach dashboard</div>
            <h1 className="mt-3 font-display font-medium text-3xl md:text-5xl tracking-tight text-slate-900">
              Hey, {coach.name.split(" ")[0]}.
            </h1>
            <p className="mt-2 text-slate-500 text-[14px]">
              Signed in as {email} · <Link to={`/coach/${coach.slug}`} className="text-[#2563EB] hover:underline">view public profile ↗</Link>
            </p>
          </div>
          <button
            onClick={onSignOut}
            data-testid={D.logout}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[12.5px] text-slate-500 hover:text-slate-900 hover:bg-white transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>

        {!stripe.payments_ready && (
          <div className="mt-6"><StripeCard stripe={stripe} onOnboard={onOnboard} compact /></div>
        )}

        {/* Tabs */}
        <Tabs current={tab} onChange={onTabChange} />

        <div className="mt-6" data-testid={D.tabPanel(tab)}>
          {tab === "overview" && (
            <OverviewPane coach={coach} upcoming={upcoming} past={past} reviews={reviews} earnings={earnings} onGoto={onTabChange} />
          )}
          {tab === "bookings" && (
            <BookingsPane
              upcoming={upcoming}
              past={past}
              cancelled={cancelled}
              onComplete={onCompleteBooking}
            />
          )}
          {tab === "availability" && (
            <AvailabilityEditor coachEmail={email} />
          )}
          {tab === "earnings" && (
            <EarningsPane earnings={earnings} stripe={stripe} onOnboard={onOnboard} past={past} upcoming={upcoming} />
          )}
          {tab === "reviews" && (
            <ReviewsPane reviews={reviews} coach={coach} />
          )}
          {tab === "profile" && (
            <ProfilePane coachEmail={email} />
          )}
          {tab === "messages" && (
            <CoachInbox coachEmail={email} />
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Tabs ------------------------------------------------------------
function Tabs({ current, onChange }) {
  return (
    <div
      role="tablist"
      aria-label="Coach dashboard sections"
      className="mt-8 bg-white border border-slate-200 rounded-2xl p-1.5 flex items-center gap-1 overflow-x-auto scroll-smooth"
    >
      {TABS.map(({ id, label, Icon }) => {
        const active = current === id;
        return (
          <button
            key={id}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(id)}
            data-testid={D.tab(id)}
            className={`shrink-0 inline-flex items-center gap-1.5 h-10 px-4 rounded-xl text-[13px] font-semibold transition-colors ${
              active
                ? "bg-slate-900 text-white"
                : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            }`}
          >
            <Icon className="w-4 h-4" strokeWidth={2.2} />
            {label}
          </button>
        );
      })}
    </div>
  );
}

// ---------- Overview --------------------------------------------------------
function OverviewPane({ coach, upcoming, past, reviews, earnings, onGoto }) {
  const completed = past.filter((b) => b.status === "completed").length;
  const nextUp = upcoming[0];
  const avg = coach.average_rating || 0;
  const cards = [
    {
      kind: "upcoming", label: "Upcoming", value: upcoming.length, sub: `${upcoming.filter((b) => b.status === "paid").length} paid`,
      Icon: CalendarClock, onClick: () => onGoto("bookings"),
    },
    {
      kind: "completed", label: "Completed", value: completed, sub: `${past.length} past total`,
      Icon: Check, onClick: () => onGoto("bookings"),
    },
    {
      kind: "pending", label: "Pending payouts", value: fmtMoney(earnings.pending_payout_cents || 0),
      sub: "Released as sessions complete", Icon: Wallet, onClick: () => onGoto("earnings"),
    },
    {
      kind: "earnings", label: "Total earnings", value: fmtMoney(earnings.net_cents || 0),
      sub: `${fmtMoney(earnings.gross_cents || 0)} gross`, Icon: TrendingUp, onClick: () => onGoto("earnings"),
    },
    {
      kind: "rating", label: "Avg rating", value: avg ? avg.toFixed(1) : "—",
      sub: avg ? "out of 5" : "No ratings yet", Icon: Star, onClick: () => onGoto("reviews"),
    },
    {
      kind: "reviews", label: "Reviews", value: reviews.length, sub: reviews.length ? "See all" : "Complete a session to earn one",
      Icon: MessageCircle, onClick: () => onGoto("reviews"),
    },
  ];
  return (
    <div data-testid={D.overviewSection}>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {cards.map((c) => (
          <button
            key={c.kind}
            type="button"
            onClick={c.onClick}
            data-testid={D.overviewCard(c.kind)}
            className="bg-white border border-slate-200 rounded-2xl p-5 text-left hover:border-slate-900 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-slate-500 font-semibold">
                <c.Icon className="w-3.5 h-3.5 text-slate-500" strokeWidth={2.4} />
                {c.label}
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300" />
            </div>
            <div className="mt-3 font-display text-[28px] md:text-3xl tracking-tight text-slate-900">{c.value}</div>
            {c.sub && <div className="mt-1 text-[12px] text-slate-500">{c.sub}</div>}
          </button>
        ))}
      </div>

      {/* Next session strip */}
      {nextUp && (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 md:p-6">
          <div className="eyebrow">§ Next session</div>
          <div className="mt-3 flex items-center gap-4 flex-wrap">
            <div className="w-11 h-11 rounded-xl bg-slate-100 grid place-items-center text-slate-600">
              {nextUp.mode === "online" ? <Video className="w-4 h-4" /> : <MapPin className="w-4 h-4" />}
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="font-semibold text-[14px] text-slate-900">{nextUp.client_name}</div>
              <div className="text-[12.5px] text-slate-500">
                {fmtDate(nextUp.session_at)} · {nextUp.duration_minutes} min · {nextUp.mode.replace("_", " ")}
              </div>
            </div>
            <button
              onClick={() => onGoto("bookings")}
              className="btn-primary h-10 px-4 rounded-full text-[13px] font-semibold inline-flex items-center gap-2"
            >
              Manage bookings <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Bookings --------------------------------------------------------
function BookingsPane({ upcoming, past, cancelled, onComplete }) {
  const [sub, setSub] = useState("upcoming");
  const list = sub === "upcoming" ? upcoming : sub === "past" ? past : cancelled;
  const empty = {
    upcoming: "No sessions on the books yet. Once clients start booking, they'll show up here.",
    past: "Once your first session is done it'll live here.",
    cancelled: "Nothing cancelled — hopefully it stays that way.",
  }[sub];
  return (
    <div>
      <SubTabs
        current={sub}
        onChange={setSub}
        items={[
          { id: "upcoming", label: `Upcoming (${upcoming.length})` },
          { id: "past", label: `Completed (${past.length})` },
          { id: "cancelled", label: `Cancelled (${cancelled.length})` },
        ]}
      />
      <div className="mt-5">
        {list.length === 0 ? (
          <Empty testId={D.emptyState(sub)} text={empty} />
        ) : (
          <ul
            data-testid={sub === "upcoming" ? D.upcomingList : sub === "past" ? D.pastList : D.cancelledList}
            className="space-y-3"
          >
            {list.slice(0, 60).map((b) => (
              <BookingRow
                key={b.id}
                b={b}
                testIdBase={D.upcomingItem(b.id)}
                completeTestId={D.completeBookingBtn(b.id)}
                onComplete={() => onComplete(b.id)}
                showComplete={sub === "upcoming"}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------- Earnings --------------------------------------------------------
function EarningsPane({ earnings, stripe, onOnboard, past, upcoming }) {
  const completedPayouts = past
    .filter((b) => b.status === "completed")
    .slice(0, 40);
  const pending = upcoming
    .filter((b) => b.status === "paid")
    .slice(0, 40);
  return (
    <div className="space-y-6">
      <StripeCard stripe={stripe} onOnboard={onOnboard} />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard icon={<DollarSign className="w-4 h-4 text-slate-500" />} label="Gross" value={<span data-testid={D.earningsGross}>{fmtMoney(earnings.gross_cents)}</span>} />
        <StatCard icon={<Wallet className="w-4 h-4 text-[#10B981]" />} label="Net (yours)" value={<span data-testid={D.earningsNet}>{fmtMoney(earnings.net_cents)}</span>} sub={`${earnings.paid_bookings} paid session${earnings.paid_bookings === 1 ? "" : "s"}`} />
        <StatCard icon={<DollarSign className="w-4 h-4 text-slate-400" />} label="Coachly fee (10%)" value={<span data-testid={D.earningsFee}>{fmtMoney(earnings.coachly_fee_cents)}</span>} />
        <StatCard icon={<Wallet className="w-4 h-4 text-amber-500" />} label="Pending payouts" value={fmtMoney(earnings.pending_payout_cents || 0)} sub="Released on session completion" />
      </div>

      <PayoutList title="Pending payouts" empty="Nothing pending — payouts release when you mark a session complete." rows={pending} kind="pending" />
      <PayoutList title="Completed payouts" empty="No completed payouts yet." rows={completedPayouts} kind="completed" />
    </div>
  );
}

function PayoutList({ title, empty, rows, kind }) {
  return (
    <div>
      <h3 className="font-display text-xl tracking-tight text-slate-900">{title}</h3>
      <div className="mt-3">
        {rows.length === 0 ? (
          <Empty testId={`payouts-empty-${kind}`} text={empty} />
        ) : (
          <ul className="space-y-2">
            {rows.map((b) => (
              <li key={b.id} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="font-medium text-[13.5px] text-slate-900 truncate">{b.client_name}</div>
                  <div className="text-[12px] text-slate-500 truncate">{fmtDate(b.session_at)} · {b.duration_minutes}m</div>
                </div>
                <div className="text-right">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Net</div>
                  <div className="font-semibold text-[14px] text-slate-900">{fmtMoney(b.net_cents, b.currency)}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------- Reviews ---------------------------------------------------------
function ReviewsPane({ reviews, coach }) {
  const avg = coach.average_rating || 0;
  return (
    <div>
      <div className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6 flex items-center gap-6 flex-wrap">
        <div>
          <div className="eyebrow">§ Rating</div>
          <div className="mt-2 flex items-end gap-3">
            <span className="font-display text-4xl md:text-5xl tracking-tight text-slate-900">
              {avg ? avg.toFixed(1) : "—"}
            </span>
            <StarsDisplay value={avg} size={14} />
          </div>
        </div>
        <div className="text-[13px] text-slate-500">
          {reviews.length} public review{reviews.length === 1 ? "" : "s"}.
        </div>
      </div>
      <div className="mt-5">
        {reviews.length === 0 ? (
          <Empty testId={D.emptyState("reviews")} text="Complete a session and ask your client for a review." />
        ) : (
          <ul data-testid={D.reviewsList} className="space-y-3">
            {reviews.map((r) => (
              <li key={r.id} className="bg-white border border-slate-200 rounded-2xl p-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-slate-100 grid place-items-center text-slate-500 font-semibold text-[12px]">
                    {(r.client_name || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-medium text-[13.5px] text-slate-900">{r.client_name}</div>
                    <div className="mt-0.5"><StarsDisplay value={r.rating} size={11} /></div>
                  </div>
                </div>
                {r.comment && <p className="mt-3 text-[13.5px] text-slate-700 whitespace-pre-wrap">{r.comment}</p>}
                {r.coach_reply && <p className="mt-3 pl-3 border-l-2 border-[#2563EB]/30 text-[13px] text-slate-500 whitespace-pre-wrap">Your reply: {r.coach_reply}</p>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------- Profile ---------------------------------------------------------
function ProfilePane({ coachEmail }) {
  return (
    <div className="space-y-2">
      <ProfileEditor coachEmail={coachEmail} />
      <IntroVideoManager coachEmail={coachEmail} />
      <MediaManager coachEmail={coachEmail} />
      <BundleManager coachEmail={coachEmail} />
    </div>
  );
}

// ---------- Shared bits -----------------------------------------------------
function SubTabs({ current, onChange, items }) {
  return (
    <div className="inline-flex bg-white border border-slate-200 rounded-full p-1 overflow-x-auto">
      {items.map(({ id, label }) => {
        const active = current === id;
        return (
          <button
            key={id}
            onClick={() => onChange(id)}
            data-testid={`bookings-subtab-${id}`}
            className={`shrink-0 h-8 px-3.5 rounded-full text-[12.5px] font-semibold transition-colors ${
              active ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function StripeCard({ stripe, onOnboard, compact }) {
  const ready = stripe.payments_ready;
  const status = stripe.onboarding_status;
  return (
    <div data-testid={D.stripeCard} className={`bg-white border border-slate-200 rounded-2xl ${compact ? "p-4 md:p-5" : "p-6 md:p-7"}`}>
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="min-w-0">
          <div className="eyebrow">§ Stripe status</div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <StatusChip status={status} ready={ready} />
            <span data-testid={D.stripeStatus} className="text-[12.5px] text-slate-500">
              {ready ? "You can receive payouts." : "Finish onboarding to start receiving payouts."}
            </span>
          </div>
          {stripe.account_id && (
            <div className="mt-3 text-[12px] font-mono text-slate-400 truncate">acct · {stripe.account_id}</div>
          )}
          {stripe.onboarding_link_sent_at && !ready && (
            <div className="mt-2 text-[11.5px] text-slate-400">
              Onboarding link sent {new Date(stripe.onboarding_link_sent_at).toLocaleString()}.
            </div>
          )}
        </div>
        <div className="shrink-0">
          {!ready && (
            <button
              onClick={onOnboard}
              data-testid={D.stripeOnboardCta}
              className="h-11 px-5 rounded-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white text-[13px] font-semibold inline-flex items-center gap-2"
            >
              <Zap className="w-4 h-4" strokeWidth={2.4} /> {stripe.connected ? "Resume Stripe onboarding" : "Connect Stripe"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusChip({ status, ready }) {
  if (ready) return <Chip cls="bg-[#10B981]/10 text-[#059669] border-[#10B981]/30" text="Live · payments active" />;
  if (status === "stripe_pending") return <Chip cls="bg-amber-50 text-amber-700 border-amber-200" text="Stripe onboarding pending" />;
  if (status === "approved") return <Chip cls="bg-blue-50 text-[#2563EB] border-blue-200" text="Approved · connect Stripe" />;
  if (status === "rejected") return <Chip cls="bg-red-50 text-red-600 border-red-200" text="Rejected" />;
  return <Chip cls="bg-slate-100 text-slate-600 border-slate-200" text={status || "Pending review"} />;
}
function Chip({ cls, text }) {
  return <span className={`inline-flex items-center h-7 px-2.5 rounded-full border text-[11.5px] font-semibold ${cls}`}>{text}</span>;
}

function StatCard({ icon, label, value, sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5">
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-[11px] tracking-[0.24em] uppercase text-slate-500 font-semibold">{label}</span>
      </div>
      <div className="mt-3 font-display text-3xl tracking-tight text-slate-900">{value}</div>
      {sub && <div className="mt-1 text-[12px] text-slate-500">{sub}</div>}
    </div>
  );
}

function Empty({ testId, text }) {
  return (
    <div data-testid={testId} className="border border-dashed border-slate-200 rounded-2xl p-10 text-center text-[13px] text-slate-500 bg-white">
      {text}
    </div>
  );
}

function BookingRow({ b, showComplete, onComplete, testIdBase, completeTestId }) {
  const isPaid = b.status === "paid";
  const isCompleted = b.status === "completed";
  const isCancelled = b.status === "cancelled";
  return (
    <li data-testid={testIdBase} className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-4 min-w-0">
        <div className="w-11 h-11 rounded-xl bg-slate-100 grid place-items-center text-slate-600">
          {b.mode === "online" ? <Video className="w-4 h-4" /> : <MapPin className="w-4 h-4" />}
        </div>
        <div className="min-w-0">
          <div className="font-medium text-[14px] text-slate-900 truncate">{b.client_name}</div>
          <div className="text-[12.5px] text-slate-500 truncate">{b.client_email}</div>
          <div className="mt-1 text-[12.5px] text-slate-700 inline-flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" strokeWidth={2} />
            {fmtDate(b.session_at)} · {b.duration_minutes} min · {b.mode.replace("_", " ")}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Payout</div>
          <div className="font-semibold text-[14px] text-slate-900">{fmtMoney(b.net_cents, b.currency)}</div>
        </div>
        {isPaid && showComplete && (
          <button
            onClick={onComplete}
            data-testid={completeTestId}
            className="h-9 px-3.5 rounded-full text-[12.5px] font-semibold bg-slate-900 text-white hover:bg-[#10B981] transition-colors inline-flex items-center gap-1.5"
          >
            <Check className="w-3.5 h-3.5" strokeWidth={2.4} /> Mark done
          </button>
        )}
        {isCompleted && (
          <span className="inline-flex items-center h-7 px-2.5 rounded-full bg-[#10B981]/10 text-[#059669] border border-[#10B981]/30 text-[11.5px] font-semibold">Completed</span>
        )}
        {isCancelled && (
          <span className="inline-flex items-center h-7 px-2.5 rounded-full bg-rose-50 text-rose-600 border border-rose-200 text-[11.5px] font-semibold">
            <X className="w-3 h-3 mr-1" /> Cancelled
          </span>
        )}
        {b.status === "pending" && (
          <span className="inline-flex items-center h-7 px-2.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 text-[11.5px] font-semibold">Pending payment</span>
        )}
      </div>
    </li>
  );
}
